import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Upload,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileKey,
  Eye,
  EyeOff,
  Trash2,
  History,
  ShieldCheck,
  Lock,
  RefreshCw,
  Info
} from 'lucide-react';
import certificateUploader from '../services/certificateUploader';
import { useAuth } from '../contexts/AuthContext';

function EmpresaCertificados() {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  // Estado general
  const [loading, setLoading] = useState(true);
  const [certificado, setCertificado] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estado para subida
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPassword, setUploadPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ step: 0, message: '' });

  // Estado para logs
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Estado para eliminar
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Estado para integridad
  const [integrity, setIntegrity] = useState(null);

  // Cargar estado del certificado
  const cargarEstado = useCallback(async () => {
    try {
      setLoading(true);
      const response = await certificateUploader.getStatus();

      if (response.success) {
        setCertificado(response.existe ? response.certificado : null);
      }
    } catch (error) {
      console.error('Error cargando estado:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarEstado();
  }, [cargarEstado]);

  // Manejar selección de archivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.p12', '.pfx'];
      const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

      if (!validExtensions.includes(extension)) {
        setMessage({ type: 'error', text: 'Solo se permiten archivos .p12 o .pfx' });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'El archivo es demasiado grande (máximo 5MB)' });
        return;
      }

      setUploadFile(file);
      setMessage({ type: '', text: '' });
    }
  };

  // Subir certificado
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadFile || !uploadPassword) {
      setMessage({ type: 'error', text: 'Selecciona un archivo e introduce la contraseña' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await certificateUploader.upload(
        uploadFile,
        uploadPassword,
        (progress) => setUploadProgress(progress)
      );

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Certificado subido correctamente'
        });
        setShowUploadForm(false);
        setUploadFile(null);
        setUploadPassword('');
        await cargarEstado();
      }

      // Mostrar advertencias si las hay
      if (result.advertencias && result.advertencias.length > 0) {
        setTimeout(() => {
          setMessage({
            type: 'warning',
            text: result.advertencias.map(w => w.message).join('. ')
          });
        }, 2000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
      setUploadProgress({ step: 0, message: '' });
    }
  };

  // Cargar logs
  const cargarLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await certificateUploader.getLogs({ limite: 50 });
      if (response.success) {
        setLogs(response.logs);
      }
    } catch (error) {
      console.error('Error cargando logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  // Toggle logs
  const toggleLogs = async () => {
    if (!showLogs) {
      await cargarLogs();
    }
    setShowLogs(!showLogs);
  };

  // Verificar integridad
  const verificarIntegridad = async () => {
    try {
      const response = await certificateUploader.verifyIntegrity();
      if (response.success) {
        setIntegrity(response.integridad);
      }
    } catch (error) {
      console.error('Error verificando integridad:', error);
    }
  };

  // Eliminar certificado
  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      setMessage({ type: 'error', text: 'Debes indicar un motivo para eliminar el certificado' });
      return;
    }

    setDeleting(true);
    try {
      const result = await certificateUploader.deleteCertificate(deleteReason);
      if (result.success) {
        setMessage({ type: 'success', text: 'Certificado eliminado correctamente' });
        setShowDeleteConfirm(false);
        setDeleteReason('');
        setCertificado(null);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setDeleting(false);
    }
  };

  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'activo': return 'text-green-600 bg-green-50';
      case 'caducado': return 'text-red-600 bg-red-50';
      case 'revocado': return 'text-gray-600 bg-gray-50';
      default: return 'text-yellow-600 bg-yellow-50';
    }
  };

  // Obtener icono del tipo de evento
  const getEventIcon = (tipo) => {
    switch (tipo) {
      case 'UPLOAD': return <Upload className="w-4 h-4 text-green-500" />;
      case 'ACCESS_GRANTED': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'ACCESS_DENIED': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'DOMAIN_BLOCKED': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'VALIDATION': return <ShieldCheck className="w-4 h-4 text-blue-500" />;
      default: return <Info className="w-4 h-4 text-gray-500" />;
    }
  };

  const goBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={goBack} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Mi Certificado Digital
            </h1>
            <p className="text-sm text-gray-600">Gestiona tu certificado de forma segura</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Mensajes */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : message.type === 'warning'
              ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {message.type === 'warning' && <AlertTriangle className="w-5 h-5" />}
            {message.type === 'error' && <XCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Info de seguridad */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Seguridad de nivel bancario</h3>
              <p className="text-sm text-blue-700 mt-1">
                Tu certificado se cifra en tu navegador antes de enviarse al servidor.
                Ni siquiera los administradores pueden ver su contenido.
                Solo se usa para operaciones con la DGT.
              </p>
            </div>
          </div>
        </div>

        {/* Estado actual del certificado */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <FileKey className="w-5 h-5 text-gray-600" />
            Estado del Certificado
          </h2>

          {certificado ? (
            <div className="space-y-4">
              {/* Estado y caducidad */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(certificado.estado)}`}>
                    {certificado.estado === 'activo' ? 'Activo' : certificado.estado}
                  </span>
                  {certificado.diasRestantes !== undefined && (
                    <span className={`flex items-center gap-1 text-sm ${
                      certificado.diasRestantes <= 30 ? 'text-red-600' :
                      certificado.diasRestantes <= 60 ? 'text-yellow-600' : 'text-gray-600'
                    }`}>
                      <Clock className="w-4 h-4" />
                      {certificado.diasRestantes > 0
                        ? `Caduca en ${certificado.diasRestantes} días`
                        : 'Caducado'}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => cargarEstado()}
                  className="text-gray-500 hover:text-gray-700"
                  title="Actualizar"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>

              {/* Detalles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Titular</label>
                  <p className="text-gray-900 font-medium">{certificado.titular}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Organización</label>
                  <p className="text-gray-900">{certificado.organizacion || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Emisor (CA)</label>
                  <p className="text-gray-900">{certificado.ca}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Tipo</label>
                  <p className="text-gray-900 capitalize">{certificado.tipo?.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Válido desde</label>
                  <p className="text-gray-900">{formatDate(certificado.validoDesde)}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Válido hasta</label>
                  <p className={`font-medium ${certificado.diasRestantes <= 30 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatDate(certificado.validoHasta)}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">CIF Validado</label>
                  <p className="flex items-center gap-1">
                    {certificado.cifValidado ? (
                      <><CheckCircle className="w-4 h-4 text-green-500" /> Sí</>
                    ) : (
                      <><AlertTriangle className="w-4 h-4 text-yellow-500" /> No</>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Subido por</label>
                  <p className="text-gray-900 text-sm">{certificado.subidoPor}</p>
                  <p className="text-gray-500 text-xs">{formatDate(certificado.subidoEl)}</p>
                </div>
              </div>

              {/* Fingerprint (truncado) */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Fingerprint</label>
                <code className="text-sm text-gray-700 font-mono">{certificado.fingerprint}</code>
              </div>

              {/* Acciones */}
              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t">
                <button
                  onClick={toggleLogs}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <History className="w-4 h-4" />
                  {showLogs ? 'Ocultar historial' : 'Ver historial de uso'}
                </button>
                <button
                  onClick={verificarIntegridad}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Verificar integridad
                </button>
                <button
                  onClick={() => setShowUploadForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Upload className="w-4 h-4" />
                  Actualizar certificado
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileKey className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay certificado configurado</h3>
              <p className="text-gray-500 mb-6">
                Sube tu certificado digital (.p12 o .pfx) para habilitar la identificación automática de conductores ante la DGT.
              </p>
              <button
                onClick={() => setShowUploadForm(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Upload className="w-5 h-5" />
                Subir certificado
              </button>
            </div>
          )}
        </div>

        {/* Integridad */}
        {integrity && (
          <div className={`bg-white rounded-xl shadow-sm p-4 mb-6 border-l-4 ${
            integrity.estado === 'VALID' ? 'border-green-500' : 'border-red-500'
          }`}>
            <div className="flex items-center gap-3">
              {integrity.estado === 'VALID' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              )}
              <div>
                <h3 className="font-medium">
                  {integrity.estado === 'VALID'
                    ? 'Integridad verificada'
                    : 'Problema de integridad detectado'}
                </h3>
                <p className="text-sm text-gray-600">
                  {integrity.registrosValidos} de {integrity.registrosTotales} registros válidos
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Historial de logs */}
        {showLogs && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              Historial de uso
            </h2>

            {logsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : logs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay registros de uso</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      log.exito ? 'bg-gray-50' : 'bg-red-50'
                    }`}
                  >
                    {getEventIcon(log.tipo)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{log.descripcion}</p>
                      {log.dominio && (
                        <p className="text-xs text-gray-500">Dominio: {log.dominio}</p>
                      )}
                      {log.error && (
                        <p className="text-xs text-red-600">{log.error}</p>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap">
                      {formatDate(log.fecha)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal de subida */}
        {showUploadForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Subir certificado
              </h2>

              <form onSubmit={handleUpload}>
                {/* Archivo */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificado digital (.p12 o .pfx)
                  </label>
                  <input
                    type="file"
                    accept=".p12,.pfx"
                    onChange={handleFileSelect}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    disabled={uploading}
                  />
                  {uploadFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                {/* Contraseña */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña del certificado
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={uploadPassword}
                      onChange={(e) => setUploadPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Introduce la contraseña"
                      disabled={uploading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Progreso */}
                {uploading && uploadProgress.message && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm text-blue-700">{uploadProgress.message}</span>
                    </div>
                    <div className="mt-2 h-2 bg-blue-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${(uploadProgress.step / 7) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Aviso de seguridad */}
                <div className="mb-6 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Tu certificado se cifra en tu navegador antes de enviarse.
                  La contraseña nunca se envía en claro.
                </div>

                {/* Botones */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadForm(false);
                      setUploadFile(null);
                      setUploadPassword('');
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={uploading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    disabled={uploading || !uploadFile || !uploadPassword}
                  >
                    {uploading ? 'Subiendo...' : 'Subir'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de confirmación de eliminación */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Eliminar certificado
              </h2>

              <p className="text-gray-600 mb-4">
                Esta acción eliminará tu certificado digital. El certificado será recuperable durante 7 días.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo de eliminación
                </label>
                <input
                  type="text"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  placeholder="Ej: Renovación, cambio de empresa..."
                  disabled={deleting}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteReason('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  disabled={deleting || !deleteReason.trim()}
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default EmpresaCertificados;
